import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import InputMask from "react-input-mask-next";
import { isEmpty } from "lodash";
import { LoadingButton } from "@mui/lab";
import { useUsuario } from "../../../providers/usuarioProvider";
import { updateDoc } from "firebase/firestore";
import UsuarioDb from "../../../db/usuarioDb";
import { IconBuildingStore } from "@tabler/icons-react";
import { Perfil } from "../../../models/enum";

interface DadosEscritorioModalProps {
  open?: boolean;
  onClose?: () => void;
}

export default function DadosEscritorioModal({
  open: controlledOpen,
  onClose: controlledOnClose,
}: DadosEscritorioModalProps = {}) {
  const { usuario, claims, isLoading, isAuthenticated } = useUsuario();
  const [website, setWebsite] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [numeroFuncionarios, setNumeroFuncionarios] = useState("");
  const [segmento, setSegmento] = useState("");
  const [origemLead, setOrigemLead] = useState("");
  const [erros, setErros] = useState({
    whatsapp: false,
    numeroFuncionarios: false,
    segmento: false,
    origemLead: false,
  });
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // This effect synchronizes the modal's visibility with the user's data state.
  useEffect(() => {
    // Default to closed.
    let shouldBeOpen = false;

    // Check if conditions to open are met.
    if (
      !isLoading &&
      isAuthenticated &&
      claims?.perfil === Perfil.CONTADOR &&
      usuario
    ) {
      const hasMissingData =
        !usuario.dadosEscritorio ||
        !usuario.dadosEscritorio.whatsapp ||
        !usuario.dadosEscritorio.numeroFuncionarios ||
        !usuario.dadosEscritorio.segmento ||
        !usuario.dadosEscritorio.origemLead;

      if (hasMissingData) {
        shouldBeOpen = true;
      }
    }

    // Only update state if it has changed to prevent re-render loops.
    if (internalOpen !== shouldBeOpen) {
      setInternalOpen(shouldBeOpen);
    }
  }, [isLoading, isAuthenticated, claims, usuario, internalOpen]);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const handleClose = () => {
    if (controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalOpen(false);
    }
  };

  const salvarDados = async () => {
    const camposComErro = camposObrigatoriosVazios();

    if (isEmpty(camposComErro) && usuario) {
      setSalvandoDados(true);
      try {
        await updateDoc(new UsuarioDb().doc({ usuarioId: usuario.id }), {
          dadosEscritorio: {
            website: website || null,
            whatsapp: whatsapp,
            numeroFuncionarios: numeroFuncionarios,
            segmento: segmento,
            origemLead: origemLead,
          },
        });

        // Limpar os campos após salvar
        setWebsite("");
        setWhatsapp("");
        setNumeroFuncionarios("");
        setSegmento("");
        setOrigemLead("");
        setErros({
          whatsapp: false,
          numeroFuncionarios: false,
          segmento: false,
          origemLead: false,
        });

        handleClose();
      } catch (error) {
        console.error("Erro ao salvar dados do escritório:", error);
      } finally {
        setSalvandoDados(false);
      }
    }
    setErros((prev) => ({ ...prev, ...camposComErro }));
  };

  const camposObrigatoriosVazios = () => {
    let camposComErro = {};
    if (!whatsapp) camposComErro = { ...camposComErro, whatsapp: true };
    if (!numeroFuncionarios)
      camposComErro = { ...camposComErro, numeroFuncionarios: true };
    if (!segmento) camposComErro = { ...camposComErro, segmento: true };
    if (!origemLead) camposComErro = { ...camposComErro, origemLead: true };
    return camposComErro;
  };

  // Só renderizar o modal se for necessário
  if (!isAuthenticated || claims?.perfil !== Perfil.CONTADOR) {
    return null;
  }

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <IconBuildingStore size={24} />
          {/* Título de dialog é h3 na escala do sistema (18px/600). */}
          <Typography variant="h3">Dados do Escritório</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="body2" color="textSecondary">
            Precisamos de algumas informações do seu escritório para completar
            ou atualizar seu cadastro na Felix na Bolsa.
          </Typography>

          <TextField
            variant="outlined"
            color="primary"
            fullWidth
            label="Website (opcional)"
            type="url"
            placeholder="https://seuescritorio.com.br"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />

          <InputMask
            mask="(99) 99999-9999"
            value={whatsapp}
            onChange={(event: any) => {
              setWhatsapp(event.target.value);
            }}
          >
            <TextField
              variant="outlined"
              color="primary"
              fullWidth
              label="WhatsApp"
              type="tel"
              error={erros.whatsapp}
              helperText={erros.whatsapp ? "Campo obrigatório" : ""}
            />
          </InputMask>

          <FormControl error={erros.numeroFuncionarios}>
            <InputLabel>Número de funcionários</InputLabel>
            <Select
              value={numeroFuncionarios}
              label="Número de funcionários"
              onChange={(e) => setNumeroFuncionarios(e.target.value)}
            >
              <MenuItem value="1-5">1-5 funcionários</MenuItem>
              <MenuItem value="6-10">6-10 funcionários</MenuItem>
              <MenuItem value="11-20">11-20 funcionários</MenuItem>
              <MenuItem value="21-50">21-50 funcionários</MenuItem>
              <MenuItem value="50+">50+ funcionários</MenuItem>
            </Select>
            {erros.numeroFuncionarios && (
              <FormHelperText>Campo obrigatório</FormHelperText>
            )}
          </FormControl>

          <FormControl error={erros.segmento}>
            <InputLabel>Segmento</InputLabel>
            <Select
              value={segmento}
              label="Segmento"
              onChange={(e) => setSegmento(e.target.value)}
            >
              <MenuItem value="contador-autonomo">Contador autônomo</MenuItem>
              <MenuItem value="escritorio-tradicional">
                Escritório tradicional
              </MenuItem>
              <MenuItem value="escritorio-investidores">
                Escritório especializado em investidores
              </MenuItem>
              <MenuItem value="agente-investimentos">
                Agente de investimentos
              </MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
            {erros.segmento && (
              <FormHelperText>Campo obrigatório</FormHelperText>
            )}
          </FormControl>

          <FormControl error={erros.origemLead}>
            <InputLabel>Como conheceu a Felix na Bolsa?</InputLabel>
            <Select
              value={origemLead}
              label="Como conheceu a Felix na Bolsa?"
              onChange={(e) => setOrigemLead(e.target.value)}
            >
              <MenuItem value="google">Pesquisando no Google</MenuItem>
              <MenuItem value="instagram">
                Vendo um anúncio no Instagram
              </MenuItem>
              <MenuItem value="evento">
                Participei de um evento ou webinar
              </MenuItem>
              <MenuItem value="indicacao">
                Recebi uma indicação de um amigo ou colega
              </MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </Select>
            {erros.origemLead && (
              <FormHelperText>Campo obrigatório</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          disabled={
            !!!whatsapp || !!!numeroFuncionarios || !!!segmento || !!!origemLead
          }
          loading={salvandoDados}
          onClick={salvarDados}
        >
          Continuar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
